import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Clock, AlertTriangle, Briefcase, TrendingUp } from 'lucide-react';

interface AgingVacancy {
  id: string;
  title: string;
  department: string;
  daysOpen: number;
  status: string;
}

interface Distribution {
  interval: string;
  count: number;
}

const VacancyAging: React.FC = () => {
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState<AgingVacancy[]>([]);
  const [distribution, setDistribution] = useState<Distribution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/recruitment-analytics/vacancy-aging');
        setVacancies(res.data.vacancies || []);
        setDistribution(res.data.distribution || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка...</div>;

  const totalVacancies = vacancies.length;
  const overdueCount = vacancies.filter(v => v.daysOpen > 45).length;
  const avgDays = totalVacancies > 0 ? Math.round(vacancies.reduce((sum, v) => sum + v.daysOpen, 0) / totalVacancies) : 0;

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
          <Clock size={24} />
        </div>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Возрастной анализ вакансий</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Контроль сроков закрытия и выявление проблемных зон</p>
        </div>
      </div>

      {/* KPI Карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><Briefcase size={22} /></div>
          <div><div style={styles.kpiLabel}>Всего открытых</div><div style={styles.kpiValue}>{totalVacancies}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}><AlertTriangle size={22} /></div>
          <div><div style={styles.kpiLabel}>Просрочено (&gt;45 дн.)</div><div style={{ ...styles.kpiValue, color: '#ef4444' }}>{overdueCount}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}><TrendingUp size={22} /></div>
          <div><div style={styles.kpiLabel}>Средний возраст</div><div style={styles.kpiValue}>{avgDays} дн.</div></div>
        </div>
      </div>

      {/* График распределения */}
      <div style={{ ...styles.glassCard, marginBottom: 24 }}>
        <h3 style={styles.sectionTitle}><TrendingUp size={18} /> Распределение по возрасту (дни)</h3>
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={distribution} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="agingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6a11cb" />
                  <stop offset="100%" stopColor="#2575fc" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
              <XAxis dataKey="interval" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
              <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.5)' }} />
              <Bar dataKey="count" fill="url(#agingGradient)" radius={[8, 8, 0, 0]} animationDuration={1000} name="Количество вакансий" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Таблица вакансий */}
      <div style={styles.glassCard}>
        <h3 style={styles.sectionTitle}><Clock size={18} /> Список открытых вакансий</h3>
        {vacancies.length === 0 ? (
          <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Нет открытых вакансий</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Название</th>
                  <th style={styles.th}>Отдел</th>
                  <th style={styles.th}>Дней в работе</th>
                  <th style={styles.th}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {vacancies.map(v => (
                  <tr
                    key={v.id}
                    onClick={() => navigate(`/app/recruitment-analytics/vacancies/${v.id}`)}
                    style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37,117,252,0.03)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={styles.td}><strong>{v.title}</strong></td>
                    <td style={styles.td}>{v.department}</td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '4px 10px', borderRadius: 12, fontWeight: 700, fontSize: 13,
                        background: v.daysOpen > 45 ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                        color: v.daysOpen > 45 ? '#ef4444' : '#10b981',
                        display: 'inline-flex', alignItems: 'center', gap: 6
                      }}>
                        {v.daysOpen} дн. {v.daysOpen > 45 && <AlertTriangle size={12} />}
                      </span>
                    </td>
                    <td style={styles.td}>{v.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: 8 },
  th: { textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  td: { padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.03)', fontSize: 14, color: '#1a1a2e' },
};

export default VacancyAging;
